import { useState, useEffect, useCallback, useMemo } from 'react';
import styles from './Formulario.module.css';
import Swal from 'sweetalert2';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import {useAuth} from '../../Auth/AuthContext';

const AniadirUniversidad = ({ abrirModal, cerrarModal, onUniversidadCreada }) => {
    const {getToken} = useAuth(); 
    const [formData, setFormData] = useState({
        nombre: '',
        pagina: '',
        direccion: '',
        locacion: '',
        tipo: '',
    });
    const token = getToken(); 

    const navigate = useNavigate();

    const [fileData, setFileData] = useState({
        logoFile: null,
        previewLogo: null,
    });

    const [tiposUniversidad, setTiposUniversidad] = useState([]); 
    const [cargandoTipos, setCargandoTipos] = useState(false);
    const [errorTipos, setErrorTipos] = useState('');

    useEffect(() => { 
        const cargarTiposUniversidad = async () => {
            setCargandoTipos(true);
            setErrorTipos('');
            
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-tipos-universidades`);

                if (!response.ok) {
                    throw new Error(`Error ${response.status}: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                if (Array.isArray(data)) {
                    setTiposUniversidad(data);
                } else if (data && Array.isArray(data.tipos)) {
                    setTiposUniversidad(data.tipos);
                } else {
                    console.error('Formato de datos inesperado:', data);
                    setErrorTipos('Formato de datos inesperado del servidor');
                    setTiposUniversidad([]);
                }
            } catch (error) {
                console.error('Error al cargar tipos de universidad:', error);
                setErrorTipos('Error al cargar los tipos de universidad');
                setTiposUniversidad([]);
            } finally {
                setCargandoTipos(false);
            }
        };

        if (abrirModal) {
            cargarTiposUniversidad();
        }
    }, [abrirModal]); 

    const [telefonos, setTelefonos] = useState(['']);
    const [correos, setCorreos] = useState(['']);
    const [isClosing, setIsClosing] = useState(false);
    
    const [errores, setErrores] = useState({
        pagina: '',
        correos: [],
        locacion: '',
        logo: '',
        telefonos: [''],
    });

    const validators = useMemo(() => ({
        url: (url) => {
            if (!url) return '';
            const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
            return !urlRegex.test(url) ? 'La URL no es válida.' : '';
        },
        
        email: (email) => {
            if (!email) return '';
            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            return !emailRegex.test(email) ? 'El correo no es válido.' : '';
        },
        
        mapEmbed: (embedUrl) => {
            if (!embedUrl) return '';
            return (!embedUrl.includes('google.com/maps/embed') && !embedUrl.includes('maps.google.com/maps'))
                ? 'Debe ser una URL de Google Maps embed válida.'
                : '';
        },
        
        telefono: (telefono) => {
            if (!telefono) return '';
            return (telefono.length < 10 || telefono.length > 15)
                ? 'El número de teléfono debe tener entre 10 y 15 dígitos.'
                : '';
        }
    }), []);

    const updateError = useCallback((field, error) => {
        setErrores(prev => ({ ...prev, [field]: error }));
    }, []);

    const updateArrayError = useCallback((field, index, error) => {
        setErrores(prev => {
            const newErrors = [...prev[field]];
            newErrors[index] = error;
            return { ...prev, [field]: newErrors };
        });
    }, []);

    const handleInputChange = useCallback((field, value) => { 
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field === 'pagina') {
            updateError('pagina', validators.url(value));
        } else if (field === 'locacion') {
            updateError('locacion', validators.mapEmbed(value));
        }
    }, [validators, updateError]);

    const handleLogoChange = useCallback((e) => {
        const file = e.target.files[0];
        
        if (!file) {
            setFileData({logoFile: null, previewLogo: null}); 
            updateError('logo', '');
            return;
        }

        if (file.type !== 'image/png') {
            updateError('logo', 'Solo se permiten archivos PNG.');
            setFileData({ logoFile: null, previewLogo: null}); 
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            updateError('logo', 'El archivo no debe superar los 5MB.');
            setFileData({logoFile: null, previewLogo:null});
            return;
        }

        setFileData(prev => ({ ...prev, logoFile: file})); 
        updateError('logo', '');
        
        const reader = new FileReader();
        reader.onload = (e) => setFileData(prev => ({ ...prev, previewLogo: e.target.result})); 
        reader.readAsDataURL(file);
    }, [updateError]);

    const eliminarLogo = useCallback(() => {
        setFileData({ logoFile: null, previewLogo: null });
        updateError('logo', '');
        
        const fileInput = document.getElementById('logo');
        if (fileInput) fileInput.value = '';
    }, [updateError]);

    const handleTelefonoChange = useCallback((index, value) => {
        const nuevosTelefonos = [...telefonos];
        nuevosTelefonos[index] = value;
        setTelefonos(nuevosTelefonos);
        updateArrayError('telefonos', index, validators.telefono(value));
    }, [telefonos, updateArrayError, validators.telefono]);

    const agregarTelefono = useCallback(() => {
        setTelefonos([...telefonos, '']);
        updateError('telefonos', [...errores.telefonos, '']);
    }, [telefonos, errores.telefonos, updateError]);

    const eliminarTelefono = useCallback((index) => {
        if (telefonos.length > 1) {
            const nuevosTelefonos = telefonos.filter((_, i) => i !== index);
            const nuevosErroresTelefonos = errores.telefonos.filter((_, i) => i !== index);
            setTelefonos(nuevosTelefonos);
            updateError('telefonos', nuevosErroresTelefonos);
        }
    }, [telefonos, errores.telefonos, updateError]);

    const handleCorreoChange = useCallback((index, valor) => {
        const nuevosCorreos = [...correos];
        nuevosCorreos[index] = valor;
        setCorreos(nuevosCorreos);
        updateArrayError('correos', index, validators.email(valor));
    }, [correos, updateArrayError, validators.email]);

    const agregarCorreo = useCallback(() => {
        setCorreos([...correos, '']);
        updateError('correos', [...errores.correos, '']);
    }, [correos, errores.correos, updateError]);

    const eliminarCorreo = useCallback((index) => {
        if (correos.length > 1) {
            const nuevosCorreos = correos.filter((_, i) => i !== index);
            const nuevosErroresCorreos = errores.correos.filter((_, i) => i !== index);
            setCorreos(nuevosCorreos);
            updateError('correos', nuevosErroresCorreos);
        }
    }, [correos, errores.correos, updateError]);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            setIsClosing(true);
        }
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsClosing(true);
    }, []);

    // Validación del formulario
    const validateForm = useCallback(() => {
        const nuevosErrores = {
            pagina: formData.pagina ? validators.url(formData.pagina) : '',
            correos: correos.map(correo => validators.email(correo)),
            locacion: formData.locacion ? validators.mapEmbed(formData.locacion) : '',
            logo: '',
            telefonos: telefonos.map(tel => validators.telefono(tel)),
        };

        const hayErrores = !!(
            nuevosErrores.pagina ||
            nuevosErrores.correos.some(error => error !== '') ||
            nuevosErrores.telefonos.some(error => error !== '') ||
            nuevosErrores.locacion
        );

        setErrores(nuevosErrores);
        return !hayErrores;
    }, [formData, correos, telefonos, validators]);

    const resetearFormulario = useCallback(() => {
    setFormData({
        nombre: '',
        pagina: '',
        direccion: '',
        locacion: '',
        tipo: '',
    });

    setFileData({
        logoFile: null,
        previewLogo: null,
    });

    setTelefonos(['']);
    setCorreos(['']);

    setErrores({
        pagina: '',
        correos: [],
        locacion: '',
        logo: '',
        telefonos: [''],
    });

    const fileInput = document.getElementById('logo');
    if (fileInput) fileInput.value = '';
    }, []);

    const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!token) { 
        setErrores(prev => ({ ...prev, token: 'No se encontró el token de autenticación.' }));
        navigate('/signin'); 
        return; 
    }
    
    if (!validateForm()) return;

    const telefonosFiltrados = telefonos
        .filter(tel => tel.trim() !== '')
        .map(tel => tel.replace(/\D/g, ''));
    const correosFiltrados = correos.filter(correo => correo.trim() !== '');

    const submitData = new FormData();
    submitData.append('nombre', formData.nombre);
    if (formData.pagina) submitData.append('pagina', formData.pagina);
    submitData.append('direccion', formData.direccion);
    submitData.append('telefonos', JSON.stringify(telefonosFiltrados));
    submitData.append('correos', JSON.stringify(correosFiltrados));
    submitData.append('locacion', formData.locacion);
    submitData.append('tipo', formData.tipo);
    if (fileData.logoFile) submitData.append('logo', fileData.logoFile);

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/crear-universidad`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${token}`
            },
            body: submitData,
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Error al crear universidad');
        }

        await response.json(); 

        await Swal.fire({
            title: 'Universidad creada correctamente.',
            icon: 'success',
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#242b57',
            customClass: { popup: 'popupbonito' },
        });

        resetearFormulario();

        if (onUniversidadCreada) {
            await onUniversidadCreada();
        }
        
        setIsClosing(true);

    } catch (error) {
        await Swal.fire({
            title: 'Error',
            text: error.message || 'Ha ocurrido un error al crear la universidad.',
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#242b57',
            customClass: { popup: 'popupbonito' },
        });
    }
}, [formData, correos, telefonos, validateForm, token, navigate, onUniversidadCreada, resetearFormulario]);

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div
                className={`${styles.modalAniadirCont} ${
                    abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''
                }`}
            >
                <div className={styles.modalScrollableAniadir}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>Añadir universidad</h2>
                    <form className={styles.formUsuario} onSubmit={handleSubmit}>
                        <label htmlFor="nombre">
                            Nombre de la universidad:
                            <input
                                type="text"
                                id="nombre"
                                value={formData.nombre}
                                placeholder="Ej: Universidad Tecnólogica de Querétaro"
                                onChange={(e) => handleInputChange('nombre', e.target.value)}
                                required
                            />
                        </label>

                        <label htmlFor="tipo">
                            Tipo de universidad: 
                            <select
                                id="tipo"
                                value={formData.tipo}
                                onChange={(e) => handleInputChange('tipo', e.target.value)}
                                required
                                disabled={cargandoTipos}
                            >
                                <option value="">
                                    {cargandoTipos ? 'Cargando tipos...' : 'Selecciona un tipo'}
                                </option>
                                {Array.isArray(tiposUniversidad) && tiposUniversidad.length > 0 ? (
                                    tiposUniversidad.map((tipo, index) => (
                                        <option key={`tipo-${tipo.id || index}`} value={tipo.id}>
                                            {tipo.nombre}
                                        </option>
                                    ))
                                ) : (
                                    !cargandoTipos && (
                                        <option value="" disabled>
                                            {errorTipos || 'No hay tipos disponibles'}
                                        </option>
                                    )
                                )}
                            </select>
                        </label>
                        {errorTipos && (
                            <p className={styles.mensajeErrorAniadir}>
                                {errorTipos}. Por favor, intenta recargar la página.
                            </p>
                        )}
                        
                        {fileData.previewLogo && (
                            <div className={styles.previewContainer}>
                                <p>Vista previa del logo:</p>
                                <div className={styles.logoPreview}>
                                    <img
                                        src={fileData.previewLogo}
                                        alt="Preview del logo"
                                        className={styles.logoImage}
                                    />
                                    <button
                                        type="button"
                                        onClick={eliminarLogo}
                                        className={styles.botonEliminarLogo}
                                        title="Eliminar logo"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        )}

                        <label htmlFor="logo">
                            Logo de la universidad (PNG, máx. 5MB):
                            <input
                                type="file"
                                id="logo"
                                accept=".png"
                                onChange={handleLogoChange}
                            />
                        </label>
                        {errores.logo && <p className={styles.mensajeErrorAniadir}>{errores.logo}</p>}

                        <label htmlFor="pagina">
                            Página web (opcional):
                            <input
                                type="url"
                                id="pagina"
                                value={formData.pagina}
                                placeholder="Ej: https://www.universidad.edu.mx"
                                onChange={(e) => handleInputChange('pagina', e.target.value)}
                            />
                        </label>
                        {errores.pagina && <p className={styles.mensajeErrorAniadir}>{errores.pagina}</p>}

                        <h3 className={styles.tituloAniadir}>Información del plantel</h3>

                        <label htmlFor="direccion">
                            Dirección  (opcional):
                            <input
                                type="text"
                                id="direccion"
                                value={formData.direccion}
                                placeholder="Ej: Av. Universidad 123, Col. Centro, Santiago de Querétaro, Qro."
                                onChange={(e) => handleInputChange('direccion', e.target.value)}
                            />
                        </label>

                        <div className={styles.seccionTelefonos}>
                            <label>Teléfonos de contacto (opcional):</label>
                            {telefonos.map((telefono, index) => (
                                <div key={`telefono-${index}`}>
                                <div className={styles.inputGroup}>
                                    <div className={styles.inputWrapper}>
                                        <PhoneInput
                                            country={'mx'}
                                            value={telefono}
                                            onChange={(value) => handleTelefonoChange(index, value)}
                                            inputClass={styles.inputTelefono}
                                            containerClass={styles.phoneInputContainer}
                                            preferredCountries={['mx', 'us', 'ca']}
                                            enableSearch={true}
                                            searchPlaceholder="Buscar país..."
                                            inputProps={{
                                                'aria-label': `Teléfono de contacto ${index + 1}`,
                                            }}
                                        />
                                        {telefonos.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => eliminarTelefono(index)}
                                                className={styles.botonEliminar}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    </div>
                                    {errores.telefonos[index] && (
                                        <p className={styles.mensajeErrorAniadir}>{errores.telefonos[index]}</p>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={agregarTelefono}
                                className={styles.botonAgregar}
                            >
                                + Agregar teléfono
                            </button>
                        </div>

                        <div className={styles.seccionCorreos}>
                            <label>Correos de contacto  (opcional): </label>
                            {correos.map((correo, index) => (
                                <div key={`correo-${index}`}>
                                    <div className={styles.inputGroup}>
                                        <input
                                            type="email"
                                            value={correo}
                                            placeholder="Ej: contacto@universidad.edu.mx"
                                            onChange={(e) => handleCorreoChange(index, e.target.value)}
                                        />
                                        {correos.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => eliminarCorreo(index)}
                                                className={styles.botonEliminar}
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    {errores.correos[index] && (
                                        <p className={styles.mensajeErrorAniadir}>{errores.correos[index]}</p>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={agregarCorreo}
                                className={styles.botonAgregar}
                            >
                                + Agregar correo
                            </button>
                        </div>

                        <label htmlFor="locacion">
                            URL de Google Maps Embed (opcional):
                            <small className={styles.helpText}>
                                Para obtener la URL: Ve a Google Maps → Busca la ubicación → Compartir → Insertar un mapa
                                → Copia la URL del src
                            </small>
                            <input
                                id="locacion"
                                value={formData.locacion}
                                placeholder="Ej: https://www.google.com/maps/embed?pb=..."
                                onChange={(e) => handleInputChange('locacion', e.target.value)}
                            />
                        </label>
                        {errores.locacion && <p className={styles.mensajeErrorAniadir}>{errores.locacion}</p>}

                        <button type="submit" className={styles.botonGuard}>
                            Guardar Universidad
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AniadirUniversidad;